import { CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LogoutOnInactivityPage from "@pages/LogoutOnInactivity/LogoutOnInactivityPage";
import MobilePage from "@pages/Mobile/MobilePage";
import * as Sentry from "@sentry/react";
import { theme } from "@theme";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { StrictMode, useEffect } from "react";
import { isMobileOnly } from "react-device-detect";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import App from "./App";
import { store } from "./app/store";
import Auth0ProviderWithNavigate from "./Auth0ProviderWithNavigate";
import ErrorPage from "./pages/Shared/ErrorPage/ErrorPage";

import "dayjs/locale/en";
import "dayjs/locale/en-au";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(relativeTime);

const adapterLocale = (region: string) => {
  switch (region.slice(0, 2)) {
    case "us":
      return "en";
    case "ap":
    default:
      return "en-au";
  }
};

const envType = process.env.VITE_ENVIRONMENT_TYPE || "unknown";
const envName = process.env.VITE_ENVIRONMENT_NAME || "unknown";
const imageTag = process.env.VITE_IMAGE_TAG || "unknown";
const gitSha = process.env.VITE_GIT_COMMIT_SHA?.slice(0, 7) || "unknown";

let release = "unknown";
switch (envType) {
  case "review":
    release = envName;
    break;
  case "qa":
  case "dev":
    release = gitSha;
    break;
  default:
    release = imageTag;
    break;
}

const browserTracing = Sentry.reactRouterV7BrowserTracingIntegration({
  useEffect,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
});

const integrations = [browserTracing];
const replayEnvs = ["preprod", "prod"];
// Only enable replay in preprod. In future, we need to enable in prod.
if (envType && replayEnvs.includes(envType)) {
  const replay = Sentry.replayIntegration({
    maskAllText: true,
    blockAllMedia: true,
  });
  integrations.push(replay);
}

Sentry.init({
  normalizeDepth: 10,
  dsn: process.env.VITE_SENTRY_DSN,
  tunnel: process.env.VITE_ORDER_SERVER_URL + "ord/sentry-tunnel/",
  integrations: integrations,
  release: release,
  environment: envType,
  replaysSessionSampleRate: parseFloat(
    process.env.VITE_SENTRY_SESSION_RATE || "0",
  ),
  replaysOnErrorSampleRate: parseFloat(
    process.env.VITE_SENTRY_ERROR_RATE || "0",
  ),
});

Sentry.setTag("region", process.env.VITE_REGION_SHORT_NAME || "unknown");

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

if (isMobileOnly) {
  if (window.location.pathname !== "/mobile") window.location.href = "/mobile";
}

const redirectToAdmin = () => {
  window.location.replace(
    new URL("/ord/admin/", process.env.VITE_ORDER_SERVER_URL).href,
  );
  return null;
};

const router = sentryCreateBrowserRouter(
  createRoutesFromChildren(
    <>
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/mobile" element={<MobilePage />} />
      <Route path="/inactivity" element={<LogoutOnInactivityPage />} />
      <Route path="/admin" element={<></>} loader={() => redirectToAdmin()} />
      <Route
        path="/*"
        element={
          <Auth0ProviderWithNavigate>
            <Sentry.ErrorBoundary
              fallback={<div>An error has occurred. Please refresh.</div>}
            >
              {!isMobileOnly && <App />}
            </Sentry.ErrorBoundary>
          </Auth0ProviderWithNavigate>
        }
      />
      <Route path="*" element={<ErrorPage />} />
    </>,
  ),
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Provider store={store}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={adapterLocale(process.env.VITE_AWS_REGION || "")}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline>
            <RouterProvider router={router} />
          </CssBaseline>
        </ThemeProvider>
      </LocalizationProvider>
    </Provider>
  </StrictMode>,
);
